<script lang="ts">
	import { categoryNameMap } from "../../../../core/schema/Category.js";
	import { getReferenceDetailPath } from "../../../../core/schema/paths/getReferenceDetailPath.js";
	import { referenceScopeLabels, referenceUseCaseLabels } from "../../../../core/schema/referenceLabels.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import Breadcrumbs from "../../../shared/components/Breadcrumbs.svelte";
	import Card from "../../../shared/components/Card.svelte";
	import Content from "../../../shared/components/Content.svelte";
	import Layout from "../components/Layout.svelte";
	import DynamicPicture from "../../../shared/components/Picture/DynamicPicture.svelte";
	import Pill from "../../../shared/components/Pill.svelte";
	import ReferenceDesktopGallery from "../../../shared/components/Reference/ReferenceDesktopGallery.svelte";
	import ReferenceMobileGallery from "../../../shared/components/Reference/ReferenceMobileGallery.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Reference.translations.json";
	import type { ReferenceProps } from "./ReferenceProps.js";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { getContext, setContext } from "svelte";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";

	const { STORAGE_URL, TENANT_LOCALE } = getGlobalSettings();
	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);

	type $$Props = ReferenceProps;

	const { reference, relatedReferences, solutions, referenceCategoryMap, breadcrumbs } = $$restProps as $$Props;

	const scopeLabels = reference.scopeLabels.map((label) => {
		const labelInMap = referenceScopeLabels[label];
		if (labelInMap) {
			return labelInMap[TENANT_LOCALE];
		}
		return label;
	});

	const useCaseLabels = reference.useCaseLabels.map((label) => {
		const labelInMap = referenceUseCaseLabels[label];
		if (labelInMap) {
			return labelInMap[TENANT_LOCALE];
		}
		return label;
	});

	const solutionLabels = referenceCategoryMap[reference.id] ?? [];

	const translate = getTranslate(translations);
	setContext(
		thumbnailImageContextKey,
		reference.previewImage ? `${STORAGE_URL}/${reference.previewImage.path}` : undefined,
	);
</script>

<Layout>
	<div class="container">
		{#if reference.previewImage}
			<section class="flex items-center overflow-hidden rounded-lg">
				<DynamicPicture
					bucket="storage"
					image={reference.previewImage}
					alt={reference.title}
					width={1424}
					height={420}
					loading="eager"
					fetchpriority="high"
					preload
					imgClass="w-full object-cover"
					sourcesets={{
						"<md": [608, 320],
						">md": [704, 580],
						">lg": [960, 420],
						">xl": [1424, 420],
					}}
				/>
			</section>
		{/if}
		<Breadcrumbs pageSlug={pageSlug[TENANT_LOCALE] ?? ""} {breadcrumbs} />
		<section class="flex flex-col md:items-center">
			<div class="lg:w-[50rem]">
				<h1 class="m-0 mb-4 p-0 font-normal lg:mb-7 lg:text-3xl lg:leading-[5rem]">
					{nbspify(reference.title)}
				</h1>
				<h2 class="text-base font-normal lg:text-[1.5rem] lg:leading-8">{nbspify(reference.perex)}</h2>
			</div>
			<div class="xl:w-[57.5rem]">
				<div class="flex w-full flex-col items-start gap-2 py-12">
					{#if solutions.length > 0 || scopeLabels.length > 0 || useCaseLabels.length > 0}
						<div class="flex w-full flex-wrap justify-between gap-3 rounded-lg bg-gray-300 p-4">
							<span class="text-2xs self-start font-normal leading-5">{translate("solutionsInstalled")}:</span>
							<span
								class="text-2xs flex w-1/6 flex-grow flex-wrap justify-end gap-1 self-start font-normal leading-5 md:w-auto"
							>
								{#each solutionLabels as solution}
									<Pill>
										{nbspify(categoryNameMap[solution][TENANT_LOCALE])}
									</Pill>
								{/each}
								{#each scopeLabels as scope}
									<Pill>
										{nbspify(scope)}
									</Pill>
								{/each}
								{#each useCaseLabels as useCase}
									<Pill>
										{nbspify(useCase)}
									</Pill>
								{/each}
							</span>
						</div>
					{/if}
					{#each reference.additionalInfo as info}
						<div class="flex w-full justify-between gap-3 rounded-lg bg-gray-300 p-4">
							{#if info.name}
								<span class="text-2xs self-start font-normal leading-5">{info.name}</span>
							{:else}
								<span class="flex-grow"></span>
							{/if}
							{#if info.value}
								<span class="text-2lg text-primary font-spaceGrotesk self-end font-normal leading-5">{info.value}</span>
							{/if}
						</div>
					{/each}
				</div>
			</div>
			<div class="lg:w-[50rem]">
				<Content content={reference.content} />
			</div>
			{#if reference.images}
				<div class="hidden md:block lg:w-[57.5rem]">
					<ReferenceDesktopGallery {reference} images={reference.images} />
				</div>
			{/if}
		</section>
	</div>
	<div class="md:hidden">
		<ReferenceMobileGallery class="md:hidden" {reference} images={reference.images} />
	</div>
	<div class="container">
		<section>
			{#if relatedReferences.length > 0}
				<div class="mt-4 flex flex-col items-center gap-10 lg:mt-16">
					<h2 class="font-spaceGrotesk mb-2 text-lg lg:text-xl">{translate("relatedReferences")}</h2>
					<div
						class="gap-2 {relatedReferences.length < 3
							? 'lg:grid-cols-2 xl:w-2/3'
							: 'grid-cols-1 lg:grid-cols-3'} {relatedReferences.length < 2 ? 'flex justify-center' : 'grid'}"
					>
						{#each relatedReferences as caseStudy}
							<Card
								title={caseStudy.title}
								description={caseStudy.perex}
								urlSlug={getReferenceDetailPath(caseStudy)}
								image={caseStudy.previewImage}
								squaredImage={relatedReferences.length < 3}
							/>
						{/each}
					</div>
				</div>
			{/if}
		</section>
	</div>
</Layout>
